import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from "yup";
import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { useAppDispatch } from '../../../../app/hooks';
import CustomInput from '../../../../components/muiComponents/customInput/CustomInput';
import { CourseWithPricingState } from '../../../../features/types';
import SingleSelect from '../../../../components/muiComponents/singleSelect/SingleSelect';
import { H4, Body2, H3 } from '../../../../layouts/TextStyles';
import { courseSalesApi, paymentKaspiOnlineApi } from '../../../../features/sales/courseSalesSlice';
import CheckoutSidebar from './components/CheckoutSidebar';
import { convertToMobile } from '../../../../utils/functions';

export default function CheckoutPage() {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const courseId = searchParams.get('courseId');
    const subjectIds = searchParams.getAll('subjectIds');
    const paymentType = searchParams.get('paymentType');

    const [loading, setLoading] = useState(false);
    const [loadingBasket, setLoadingBasket] = useState(false);
    const [basketData, setBasketData] = useState<CourseWithPricingState | null>(null);
    const [courseSubjects, setCourseSubjects] = useState([]);
    const [promocode, setPromocode] = useState('');
    const [checkingPromocode, setCheckingPromocode] = useState(false);
    const [qrBase64, setQrBase64] = useState<string | null>(null);
    const [kaspiRedirectUrl, setKaspiRedirectUrl] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Fetch basket via RTK Query hook
    const {
        data: basketResponse,
        isFetching: isFetchingBasket,
    } = courseSalesApi.useFetchBasketPriceQuery(
        {
            courseId: Number(courseId),
            subjectIds: subjectIds.map((id) => Number(id)),
        },
        {
            skip: !courseId || subjectIds.length === 0,
        }
    );

    const validationSchema = yup.object().shape({
        firstName: yup.string().required(formatMessage({ id: 'isRequired' })),
        lastName: yup.string().required(formatMessage({ id: 'isRequired' })),
        mobile: yup.string().required(formatMessage({ id: 'isRequired' })),
        language: yup.string().required(formatMessage({ id: 'isRequired' }))
    });

    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            mobile: "+7 (7",
            language: "",
            address: ""
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                // Create abort controller for request cancellation
                abortControllerRef.current = new AbortController();

                const buyInit = courseSalesApi.endpoints.buyCourse.initiate({
                    courseId: Number(courseId),
                    subjectIds: subjectIds.map((id) => Number(id)),
                    isMonthly: paymentType === 'monthly',
                    firstName: values.firstName,
                    lastName: values.lastName,
                    mobile: convertToMobile(values.mobile),
                    language: values.language,
                    address: values.address || undefined,
                    promocode: basketData?.promocodeValid ? promocode : undefined,
                });
                const sub = dispatch(buyInit);
                const response: any = await sub.unwrap().catch(() => null);

                console.log('📥 Ответ от buyCourse:', response);

                if (response?.success && response?.data?.invoiceId) {
                    const KASPI_RETURN_URL = `${window.location.origin}/sell-course/payment-status`;
                    const tranId = String(response.data.invoiceId);
                    const orderId = String(response.data.invoiceId);
                    const amountTiyn = Math.round(Number(response.data.amount) * 100);

                    console.log('🔄 Инициализация Kaspi платежа...', { tranId, orderId, amountTiyn });

                    try {
                        const kaspiInit = paymentKaspiOnlineApi.endpoints.initKaspiPayment.initiate({
                            TranId: tranId,
                            OrderId: orderId,
                            Amount: amountTiyn,
                            Service: 'EduconOnline',
                            returnUrl: KASPI_RETURN_URL,
                            refererHost: 'dostyq.online',
                            GenerateQrCode: true,
                        });
                        console.log('📤 Отправка запроса к Kaspi API...');
                        const kaspiSub = dispatch(kaspiInit);
                        const kaspiResp: any = await kaspiSub.unwrap().catch((error) => {
                            console.error('❌ Ошибка Kaspi API:', error);
                            return null;
                        });
                        console.log('📥 Ответ от Kaspi API:', kaspiResp);

                        if (kaspiResp?.code === 0) {
                            if (kaspiResp?.redirectUrl) setKaspiRedirectUrl(kaspiResp.redirectUrl);
                            if (kaspiResp?.qrCodeImage) setQrBase64(kaspiResp.qrCodeImage);

                            // Показываем QR и URL, не делаем автоматический редирект
                            console.log('✅ Kaspi платеж инициализирован:', {
                                redirectUrl: kaspiResp?.redirectUrl,
                                hasQR: !!kaspiResp?.qrCodeImage
                            });
                        } else {
                            toast.error(kaspiResp?.message || formatMessage({ id: 'payment_init_failed' }));
                        }
                    } catch (e) {
                        toast.error(formatMessage({ id: 'payment_init_failed' }));
                    }
                }
            } finally {
                setLoading(false);
                abortControllerRef.current = null;
            }
        },
    });


    // Sidebar helpers (removed unused bonus courses)

    const handleChangeMobile = (value: string) => {
        formik.setFieldValue('mobile', value);
    };

    // Removed SMS confirmation flow per requirements

    const handleCheckPromocode = async () => {
        setCheckingPromocode(true);
        try {
            const res = await courseSalesApi.endpoints.fetchBasketPrice.initiate({
                courseId: Number(courseId),
                subjectIds: subjectIds.map((id) => Number(id)),
                promocode: promocode?.length ? promocode : undefined,
            });
            const sub = dispatch(res);
            const result: any = await sub.unwrap().catch(() => null);
            if (result) {
                setBasketData(result?.Course?.[0] || result?.landingCoursePrice || null);
                setCourseSubjects(result?.CourseSubject || result?.courseSubjects || []);
            } else {
                toast.error(formatMessage({ id: 'promocodeNotFound' }));
            }
        } finally {
            setCheckingPromocode(false);
        }
    };

    useEffect(() => {
        setLoadingBasket(isFetchingBasket);
    }, [isFetchingBasket]);

    useEffect(() => {
        // Map basket response into local sidebar-friendly state (support both API shapes)
        if (basketResponse) {
            const anyRes: any = basketResponse as any;
            const courseFromBasket = anyRes?.Course?.[0] || anyRes?.landingCoursePrice || null;
            const subjectsFromBasket = anyRes?.CourseSubject || anyRes?.courseSubjects || [];
            setBasketData(courseFromBasket);
            setCourseSubjects(subjectsFromBasket);
        }
    }, [basketResponse]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const languageOptions = [
        { id: 'KZ', name: formatMessage({ id: 'lang_kz' }) },
        { id: 'RU', name: formatMessage({ id: 'lang_ru' }) }
    ];

    return (
        <Wrapper>
            <Content>
                <Header>
                    <Title>
                        <H3>{formatMessage({ id: 'checkout-title' })}</H3>
                    </Title>
                </Header>
                <CheckoutForm onSubmit={formik.handleSubmit}>
                    <FormContainer>
                        <FormRow>
                            <FormGroup>
                                <FormLabel htmlFor="firstName">
                                    {formatMessage({ id: 'name_label' })} <RequiredSpan>*</RequiredSpan>
                                </FormLabel>
                                <CustomInput
                                    name="firstName"
                                    value={formik.values.firstName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder={formatMessage({ id: 'name_label' })}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                />
                                <ErrorText>{formik.touched.firstName && formik.errors.firstName}</ErrorText>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel htmlFor="lastName">
                                    {formatMessage({ id: 'surname_label' })} <RequiredSpan>*</RequiredSpan>
                                </FormLabel>
                                <CustomInput
                                    name="lastName"
                                    value={formik.values.lastName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder={formatMessage({ id: 'surname_label' })}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                />
                                <ErrorText>{formik.touched.lastName && formik.errors.lastName}</ErrorText>
                            </FormGroup>
                        </FormRow>
                        <FormRow>
                            <FormGroup>
                                <FormLabel htmlFor="mobile">
                                    {formatMessage({ id: 'phone_label' })} <RequiredSpan>*</RequiredSpan>
                                </FormLabel>
                                <CustomInput
                                    name="mobile"
                                    value={formik.values.mobile}
                                    onChange={(e) => handleChangeMobile(e.target.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder={formatMessage({ id: 'phone_placeholder' })}
                                    type="tel"
                                    error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                                />
                                <ErrorText>{formik.touched.mobile && formik.errors.mobile}</ErrorText>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel htmlFor="address">Адрес</FormLabel>
                                <CustomInput
                                    name="address"
                                    value={formik.values.address}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder="Адрес"
                                />
                            </FormGroup>
                        </FormRow>
                        <FormRow>
                            <FormGroup>
                                <FormLabel htmlFor="language">
                                    {formatMessage({ id: 'course_lang_label' })} <RequiredSpan>*</RequiredSpan>
                                </FormLabel>
                                <SingleSelect
                                    id="language"
                                    name="language"
                                    placeholder={formatMessage({ id: 'course_lang_label' })}
                                    value={formik.values.language}
                                    handleChange={formik.handleChange}
                                    handleBlur={formik.handleBlur}
                                    handleError={formik.touched.language && Boolean(formik.errors.language)}
                                    menuItems={languageOptions}
                                    valueField="id"
                                />
                                <ErrorText>{formik.touched.language && formik.errors.language}</ErrorText>
                            </FormGroup>

                        </FormRow>

                        <InfoMessage>
                            <InfoIcon src="/icons/alert-icon.svg" alt="info" />
                            <Body2 color="#4D5464">
                                {formatMessage({ id: 'info_message_part1' })}
                                <HighlightText>{formatMessage({ id: 'info_message_highlight' })}</HighlightText>
                                {formatMessage({ id: 'info_message_part2' })}
                            </Body2>
                        </InfoMessage>

                        <TermsAgreement>
                            <TermsLabel htmlFor="terms">
                                {formatMessage({ id: 'terms_agree_part1' })}
                                <TermsLink>
                                    {formatMessage({ id: 'terms_agree_highlight' })}
                                </TermsLink>
                                {formatMessage({ id: 'terms_agree_part2' })}
                            </TermsLabel>
                        </TermsAgreement>
                    </FormContainer>

                    <CheckoutSidebar
                        paymentType={paymentType}
                        formik={formik}
                        courseSubjects={courseSubjects}
                        course={basketData}
                        loadingBasket={loadingBasket}
                        buying={loading}
                        checkingPromocode={checkingPromocode}
                        promocode={promocode}
                        setPromocode={setPromocode}
                        checkPromocode={handleCheckPromocode}
                    />
                </CheckoutForm>

                {/* TODO: Implement Terms Dialog */}
                {/* <TermsDialog 
                isOpen={isTermsDialogOpen}
                onClose={() => setIsTermsDialogOpen(false)}
                /> */}
                {kaspiRedirectUrl && (
                    <PaymentBox>
                        <PaymentRow>
                            <PaymentLabel>{formatMessage({ id: 'kaspi_redirect_url' })}:</PaymentLabel>
                            <PaymentUrlReadOnly value={kaspiRedirectUrl} readOnly onFocus={(e) => e.currentTarget.select()} />
                            <CopyButton
                                type="button"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(kaspiRedirectUrl);
                                        toast.success(formatMessage({ id: 'copied_to_clipboard' }));
                                    } catch (err) {
                                        console.error('Ошибка копирования:', err);
                                        toast.error(formatMessage({ id: 'copy_failed' }));
                                    }
                                }}
                            >
                                {formatMessage({ id: 'copy' })}
                            </CopyButton>
                        </PaymentRow>
                        {qrBase64 && (
                            <QRWrapper>
                                <img alt="Kaspi QR" src={`data:image/png;base64,${qrBase64}`} />
                            </QRWrapper>
                        )}

                    </PaymentBox>
                )}
            </Content>
        </Wrapper>
    );
}


const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Title = styled.div`
    text-align: left;
    display: flex;
    align-items: center;
`

const Content = styled.div`
    background-color: #fff;
    border-radius: 16px;
    padding: 20px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-bottom: 20px;
`

const CheckoutForm = styled.form`
    display: flex;
    flex: 1;
    gap: 24px;
    padding: 24px;
    
`;

const FormContainer = styled.div`
    flex: 1;
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FormRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
`;

const FormLabel = styled.label`
    font-weight: 500;
    margin-bottom: 8px;
    color: #333;
`;

const RequiredSpan = styled.span`
    color: red;
`;

const ErrorText = styled.div`
    color: red;
    font-size: 12px;
    margin-top: 4px;
`;

const InfoMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: #f0f8ff;
    padding: 16px;
    border-radius: 8px;
    margin: 16px 0;
`;

const InfoIcon = styled.img`
    width: 24px;
    height: 24px;
`;

const HighlightText = styled.span`
    color: #004EFF;
    font-weight: 600;
`;

const TermsAgreement = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin: 16px 0;
`;

const TermsLabel = styled.label`
    color: #4D5464;
    font-size: 14px;
    line-height: 1.4;
`;

const TermsLink = styled.span`
    color: #004EFF;
    cursor: pointer;
    text-decoration: underline;
`;

// Removed unused styled components: ApprovePhoneButton, TimerText, CodeInput

const PaymentBox = styled.div`
    margin: 16px 0;
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fafafa;
`;

const PaymentRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 12px;
`;

const PaymentLabel = styled.div`
    font-weight: 600;
`;

const PaymentUrlReadOnly = styled.input`
    flex: 1;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: #fff;
`;

const CopyButton = styled.button`
    padding: 8px 12px;
    background: #4D60D7;
    color: #fff;
    border: none;
    border-radius: 6px;
    cursor: pointer;
`;

const QRWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 8px;
    > img { width: 240px; height: 240px; }
`;
